/**
 * Client-side Pexels helpers shared by the StockPhotoDialog (#21305 presentation port).
 *
 * The actual search happens through the authenticated `pexelsSearch` GraphQL resolver so the
 * PEXELS_API_KEY stays server-side — this module only holds the response types and the pure
 * helpers the dialog needs (upload-url building, masonry column distribution, result guards).
 */

export interface PexelsPhotoSrc {
  original: string
  large2x?: string | null
  large?: string | null
  medium?: string | null
  small?: string | null
  portrait?: string | null
  landscape?: string | null
  tiny?: string | null
}

export interface PexelsPhoto {
  id: number
  width?: number | null
  height?: number | null
  alt?: string | null
  avgColor?: string | null
  photographer?: string | null
  photographerUrl?: string | null
  src: PexelsPhotoSrc
}

export interface PexelsSearchResult {
  totalResults: number
  page: number
  perPage: number
  photos: PexelsPhoto[]
}

/**
 * A photo prepared for the masonry grid: a display `src`, its intrinsic dimensions (so columns
 * can be balanced by aspect ratio), and an `onClick` handler.
 */
export interface AlbumPhoto {
  key: string
  src: string
  width: number
  height: number
  alt?: string | null
  photographer?: string | null
  onClick: () => void
}

/**
 * Cloudinary fetches the chosen photo from this URL server-side, so we request a
 * reasonably-sized, compressed original (mirrors the legacy Pexels flow's transform).
 */
export function getPexelsUploadUrl(src: PexelsPhotoSrc): string {
  return `${src.original}?auto=compress&cs=tinysrgb&h=1600&w=1280`
}

/** Hostnames Pexels actually serves photo bytes from. */
const TRUSTED_PEXELS_HOSTS = ['images.pexels.com']

/**
 * Guards the client-side `fetch()` in StockPhotoPanel (used to build a real File/Blob for the S3
 * upload path, since S3 — unlike Cloudinary — can't fetch a remote URL server-side). `photo.src`
 * ultimately comes from our own `pexelsSearch` resolver, but this is cheap defense-in-depth
 * against a tampered/forged src ever driving the browser to fetch (and then upload) an arbitrary
 * URL.
 */
export function isTrustedPexelsUrl(url: string): boolean {
  try {
    return TRUSTED_PEXELS_HOSTS.includes(new URL(url).hostname)
  } catch {
    return false
  }
}

export function searchWasSuccessful(result?: PexelsSearchResult | null): result is PexelsSearchResult {
  return !!result && Array.isArray(result.photos)
}

/**
 * True when the server reports more results than we've loaded and we actually have some — used
 * to decide whether to keep listening for the infinite-scroll load-more.
 */
export function hasMoreResults(result?: PexelsSearchResult | null): boolean {
  if (!searchWasSuccessful(result)) return false
  return result.photos.length > 0 && result.totalResults > result.photos.length
}

/**
 * Distribute items into `columnCount` masonry columns, always appending the next item to the
 * currently-shortest column so column heights stay balanced. Order is preserved within a column.
 * Height is estimated from each item's aspect ratio (falls back to a square when unknown).
 */
export function distributeToColumns<T extends { width?: number | null; height?: number | null }>(
  items: T[],
  columnCount: number
): T[][] {
  const columns: T[][] = []
  const heights: number[] = []
  const count = Math.max(1, Math.floor(columnCount) || 1)

  for (let i = 0; i < count; i++) {
    columns.push([])
    heights.push(0)
  }

  for (const item of items) {
    const width = item.width && item.width > 0 ? item.width : 1
    const height = item.height && item.height > 0 ? item.height : width
    const aspect = height / width

    let shortest = 0
    for (let i = 1; i < count; i++) {
      if (heights[i] < heights[shortest]) shortest = i
    }

    columns[shortest].push(item)
    heights[shortest] += aspect
  }

  return columns
}
